import IO from '../tablet/IO';
import OS from '../tablet/OS';
import ActivityLoader from '../editor/activity/engine/ActivityLoader';
import ActivityStorageService from '../editor/activity/storage/ActivityStorageService';
import {
    buildProjectRecordFromTemplate,
    normalizeBackground,
    normalizeSprite
} from '../editor/activity/shared/ActivityTemplateProjectBuilder';
import {gn} from '../utils/lib';

export default class ActivityProjectService {
    static async openLesson (lesson, courseId, unitId) {
        const activityPath = `courses/${courseId}/units/${unitId}/${lesson.file}`;
        const activityResult = await ActivityLoader.loadActivity(activityPath);
        if (!activityResult.valid) {
            throw new Error((activityResult.errors || ['Unable to load activity']).join(', '));
        }

        const activity = activityResult.data;
        const title = ActivityProjectService._getText(activity.title, lesson.title || lesson.activityId);
        const progress = await ActivityStorageService.getActivityProgress(lesson.activityId) || {};
        let shouldResetResumeState = false;

        let projectId = await ActivityStorageService.getActivityProjectId(lesson.activityId) || progress.projectId;
        if (projectId && !(await ActivityProjectService._projectExists(projectId))) {
            projectId = null;
            shouldResetResumeState = true;
        }
        if (projectId && !(await ActivityProjectService._isCompatibleActivityProject(projectId, activity))) {
            projectId = null;
            shouldResetResumeState = true;
        }

        if (!projectId) {
            projectId = await ActivityProjectService._createProject(activity, title);
            shouldResetResumeState = progress.status !== 'completed';
        }

        await ActivityStorageService.saveActivityProjectId(lesson.activityId, projectId);
        await ActivityStorageService.saveProgress(
            lesson.activityId,
            ActivityProjectService._buildLessonProgress(
                progress,
                projectId,
                activityPath,
                title,
                shouldResetResumeState
            )
        );

        ActivityProjectService._navigateToEditor(
            projectId,
            lesson.activityId,
            activityPath,
            activity.type || 'guided-creation'
        );
    }

    static _buildLessonProgress (progress, projectId, activityPath, title, shouldResetResumeState) {
        const base = {
            status: progress.status === 'completed' ? 'completed' : 'in_progress',
            attempts: 0,
            projectId,
            activityPath,
            title,
            lastOpenedAt: new Date().toISOString()
        };

        if (!shouldResetResumeState || progress.status === 'completed') {
            return base;
        }

        return {
            ...base,
            currentState: 'INTRO',
            scriptSnapshot: '',
            flagTapCount: 0,
            maxLoopDepth: 0,
            parameterChanges: 0,
            duplicateBlocksRemoved: 0,
            reflectionPromptsFired: 0,
            phase3RunCount: 0,
            loopDiscovered: false,
            repeatParameterChanged: false,
            multiActionLoop: false,
            outcomeFlags: {},
            completedAt: null,
            sessionStartedAt: Date.now(),
            idleTimeMs: 0
        };
    }

    static async resetLesson (activityId) {
        if (!activityId) {
            return false;
        }

        const resetResult = await ActivityStorageService.resetActivity(activityId);
        if (resetResult && resetResult.projectId) {
            await ActivityProjectService._deleteProject(resetResult.projectId);
        }

        return true;
    }

    static async _projectExists (projectId) {
        const result = await new Promise((resolve) => {
            IO.getObjectinDB('projects', projectId, resolve);
        });

        try {
            const rows = JSON.parse(result);
            return Array.isArray(rows) && rows.length > 0;
        } catch (_) {
            return false;
        }
    }

    static async _createProject (activity, title) {
        const starterPath = activity.starterProjectPath ||
            (activity.metadata && activity.metadata.starterProjectPath);
        const template = starterPath
            ? await ActivityProjectService._loadTemplateProject(starterPath)
            : ActivityProjectService._buildProjectFromActivityTemplate(activity, title);

        const uniqueName = await ActivityProjectService._uniqueProjectName(title || template.name || 'Activity');
        const projectData = {
            name: uniqueName,
            version: template.version || 'iOSv01',
            json: template.json,
            thumbnail: template.thumbnail,
            isgift: '0'
        };

        return await new Promise((resolve) => {
            IO.createProject(projectData, resolve);
        });
    }

    static async _isCompatibleActivityProject (projectId, activity) {
        const project = await ActivityProjectService._loadProject(projectId);
        if (!project || !project.json || !Array.isArray(project.json.pages) || project.json.pages.length < 1) {
            return false;
        }

        const firstPageId = project.json.currentPage || project.json.pages[0];
        const firstPage = project.json[firstPageId];
        if (!firstPage) {
            return false;
        }

        const templatePages = activity.projectTemplate && Array.isArray(activity.projectTemplate.pages)
            ? activity.projectTemplate.pages
            : [];
        const templatePage = templatePages[0] || null;
        if (!templatePage) {
            return true;
        }
        if (project.json.pages.length < templatePages.length) {
            return false;
        }

        const actualBackground = ActivityProjectService._normalizeBackground(firstPage.md5);
        const templateBackground = ActivityProjectService._normalizeBackground(templatePage.background);
        const templateSprites = Array.isArray(templatePage.sprites) ? templatePage.sprites : [];
        const actualSpriteIds = Array.isArray(firstPage.sprites) ? firstPage.sprites : [];

        if (actualSpriteIds.length !== templateSprites.length) {
            return false;
        }

        if (templateBackground && actualBackground !== templateBackground) {
            return false;
        }

        let expectedPlayerId = '';
        for (let index = 0; index < templateSprites.length; index++) {
            const templateSprite = templateSprites[index] || {};
            const actualSpriteId = actualSpriteIds[index];
            const actualSprite = actualSpriteId ? firstPage[actualSpriteId] : null;
            if (!actualSprite) {
                return false;
            }

            const expectedSpriteMd5 = ActivityProjectService._normalizeSprite(
                templateSprite.md5 || `${templateSprite.name}.svg`
            );
            const actualSpriteMd5 = ActivityProjectService._normalizeSprite(
                actualSprite.md5 || `${actualSprite.name}.svg`
            );

            if (expectedSpriteMd5 !== actualSpriteMd5) {
                return false;
            }

            if (templateSprite.role && actualSprite.role !== templateSprite.role) {
                return false;
            }

            if (typeof templateSprite.x === 'number' && !ActivityProjectService._matchesCoordinate(actualSprite.homex, templateSprite.x)) {
                return false;
            }
            if (typeof templateSprite.y === 'number' && !ActivityProjectService._matchesCoordinate(actualSprite.homey, templateSprite.y)) {
                return false;
            }

            if (!expectedPlayerId && templateSprite.role === 'player') {
                expectedPlayerId = actualSpriteId;
            }
        }

        if (expectedPlayerId && firstPage.currentSpriteName && firstPage.currentSpriteName !== expectedPlayerId) {
            return false;
        }
        if (expectedPlayerId && firstPage.lastSprite && firstPage.lastSprite !== expectedPlayerId) {
            return false;
        }

        return true;
    }

    static async _loadProject (projectId) {
        const result = await new Promise((resolve) => {
            IO.getObjectinDB('projects', projectId, resolve);
        });

        try {
            const rows = JSON.parse(result);
            if (!Array.isArray(rows) || rows.length < 1) {
                return null;
            }

            const project = IO.parseProjectData(rows[0]);
            if (typeof project.json === 'string') {
                project.json = JSON.parse(project.json);
            }
            return project;
        } catch (_) {
            return null;
        }
    }

    static async _loadTemplateProject (templatePath) {
        const raw = await new Promise((resolve, reject) => {
            IO.getObject(templatePath, (response) => {
                if (!response) {
                    reject(new Error(`Missing starter project: ${templatePath}`));
                    return;
                }
                resolve(response);
            });
        });

        const parsed = JSON.parse(raw);
        const template = IO.parseProjectData(parsed[0]);
        if (typeof template.json === 'string') {
            template.json = JSON.parse(template.json);
        }
        if (typeof template.thumbnail === 'string') {
            template.thumbnail = JSON.parse(template.thumbnail);
        }
        return template;
    }

    static _buildProjectFromActivityTemplate (activity, title) {
        return buildProjectRecordFromTemplate(title, activity.projectTemplate || {});
    }

    static _normalizeBackground (value) {
        return normalizeBackground(value);
    }

    static _normalizeSprite (value) {
        return normalizeSprite(value);
    }

    static _matchesCoordinate (actual, expected) {
        return typeof actual === 'number' && Math.abs(actual - expected) <= 1;
    }

    static async _uniqueProjectName (baseName) {
        return await new Promise((resolve) => {
            IO.uniqueProjectName({name: baseName}, (result) => {
                resolve(result.name || baseName);
            }, false);
        });
    }

    static async _deleteProject (projectId) {
        if (!projectId) {
            return;
        }

        ActivityProjectService._cleanupProjectFiles(projectId);

        await new Promise((resolve) => {
            try {
                IO.deleteobject(OS.database, projectId, function () {
                    resolve(true);
                });
            } catch (_) {
                resolve(true);
            }
        });

        try {
            if (window.dbModule && window.dbModule.projectCache) {
                window.dbModule.projectCache.delete(projectId);
            }
        } catch (_) {}
    }

    static _cleanupProjectFiles (projectId) {
        if (typeof localStorage === 'undefined' || !projectId) {
            return;
        }

        const prefixes = [
            `scratchjr_file_${projectId}`,
            `scratchjr_media_${projectId}`,
            `scratchjr_asset_${projectId}`,
            `scratchjr_medianame_${projectId}`,
            `projects_${projectId}`
        ];

        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) {
                continue;
            }
            if (prefixes.some((prefix) => key.indexOf(prefix) === 0)) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach((key) => {
            try {
                localStorage.removeItem(key);
            } catch (_) {}
        });
    }

    static _navigateToEditor (projectId, activityId, activityPath, activityType) {
        const scrollTop = gn('wrapc') ? gn('wrapc').scrollTop : 0;
        const query = new URLSearchParams({
            pmd5: projectId,
            mode: 'activity',
            activity: activityId,
            path: activityPath,
            type: activityType
        });

        OS.setfile('homescroll.sjr', scrollTop, function () {
            window.location.href = `editor.html?${query.toString()}`;
        });
    }

    static _getText (value, fallback) {
        if (value && typeof value === 'object') {
            return value.en || value['en-US'] || Object.values(value)[0] || fallback;
        }
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
        return fallback;
    }
}
